import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useCurrentUser from '@hooks/useCurrentUser'
import useProviderProductCode, { generateUrls } from '@hooks/useProviderProductCode'
import useProviderProductCodes from '@hooks/useProviderProductCodes'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import ProviderProductCodeListItem from '../ProviderProductCodeListItem'

const buildPageFilterFields = () => {
  const boxProps = { marginBottom: 'large' }
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Product Code',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        endpoint: 'service_products',
        entityKey: 'serviceProducts',
      },
      key: 'service_product_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const providerProductCodesPayload = useProviderProductCodes({
    filters: pageFilters,
    performHttpRequests: true,
    requestOptions: { include_provider_product_code_product: true },
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredProviderProductCodes,
    filteredProviderProductCodesCount,
    hasFilteredProviderProductCodes,
    loading,
  } = providerProductCodesPayload

  const { isProviderDirector, provider } = useCurrentUser()

  const {
    callbacks: {
      createOrEditProviderProductCode,
    },
  } = useProviderProductCode({ provider_id: isProviderDirector ? provider?.id : null })

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { providerProductCodesIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Product Codes | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => createOrEditProviderProductCode()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey="providerProductCodes"
        tabBarItems={[
          {
            href: providerProductCodesIndexUrl,
            icon: faCheckCircle,
            key: 'providerProductCodes',
            title: 'Product Codes',
          },
        ]}
        title="Product Codes"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            {/*
             // @ts-expect-error TS2322 */}
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredProviderProductCodes && <ListBlankState />}

            {!loading && hasFilteredProviderProductCodes && (
              <DashboardModule title="Product Codes">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredProviderProductCodes.map(providerProductCode => (
                      <ProviderProductCodeListItem
                        key={providerProductCode.id}
                        providerProductCode={providerProductCode}
                      />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredProviderProductCodesCount}
                    // @ts-expect-error TS2322
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />

            <SidebarNoticeBox boxProps={{ variant: 'white' }}>
              <SidebarNoticeBox.Title>Product Codes</SidebarNoticeBox.Title>

              <SidebarNoticeBox.Paragraph>
                Product codes can be used to map order items between Realhub and the provider&apos;s system.
                Realhub will return these codes in the <strong>provider_product_code</strong> node in the json api,
                webhooks and order approval email.
              </SidebarNoticeBox.Paragraph>
            </SidebarNoticeBox>
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
